import { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
} from "@mui/material";
import { AccountBalanceWallet } from "@mui/icons-material";
import WithdrawalProofDialog from "./WithdrawalProofDialog";

export interface WithdrawalRequest {
  id: number;
  amount: string;
  payout_method: string | null;
  payout_destination: string | null;
  status: string;
  requested_at: string;
  requested_by_email: string;
  campaign_currency: string;
}

interface WithdrawalBannerProps {
  withdrawal: WithdrawalRequest;
  onApprove: (id: number, proof: File) => Promise<void>;
  onDismiss: (id: number) => void;
  actionLoading: boolean;
}

const WithdrawalBanner = ({
  withdrawal,
  onApprove,
  onDismiss,
  actionLoading,
}: WithdrawalBannerProps) => {
  const [proofDialogOpen, setProofDialogOpen] = useState(false);
  const currency = withdrawal.campaign_currency === "ZWL" ? "ZWL " : "$";
  const amount = parseFloat(withdrawal.amount || "0").toFixed(2);
  const requester = withdrawal.requested_by_email?.split("@")[0] || "Someone";
  const method = withdrawal.payout_method || "their account";

  return (
    <Paper
      elevation={0}
      sx={{
        px: 3,
        py: 4,
        borderRadius: 3,
        mb: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",

        bgcolor: "#fff",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <AccountBalanceWallet
          sx={{ color: "#d97706", fontSize: 22, flexShrink: 0 }}
        />
        <Typography variant="body2" color="text.primary">
          <strong>{requester}</strong> has requested a withdrawal of{" "}
          <strong>
            {currency}
            {amount}
          </strong>{" "}
          via <strong>{method}</strong> — waiting for approval
        </Typography>
      </Box>

      <Box
        sx={{ display: "flex", gap: 1.5, alignItems: "center", flexShrink: 0 }}
      >
        <Button
          variant="contained"
          size="small"
          disabled={actionLoading}
          onClick={() => setProofDialogOpen(true)}
          sx={{
            textTransform: "none",
            borderRadius: 5,
            boxShadow: 0,
            px: 2.5,
            py: 1,
            bgcolor: "#0D9488",
            "&:hover": { bgcolor: "#2d8c84" },
            minWidth: 100,
          }}
        >
          {actionLoading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            "Approve"
          )}
        </Button>
        <Button
          variant="text"
          size="small"
          disabled={actionLoading}
          onClick={() => onDismiss(withdrawal.id)}
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          Dismiss
        </Button>
      </Box>

      <WithdrawalProofDialog
        open={proofDialogOpen}
        onClose={() => setProofDialogOpen(false)}
        onSubmit={(file) => onApprove(withdrawal.id, file)}
      />
    </Paper>
  );
};

export default WithdrawalBanner;
